import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { imgSrc } from '../lib/imgSrc';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AddMaintenanceDialog from '../components/AddMaintenanceDialog';
import EditMaintenanceDialog from '../components/EditMaintenanceDialog';
import EditMachineryDialog from '../components/EditMachineryDialog';
import InstallationPointsPanel from '../components/InstallationPointsPanel';
import { EquipmentHistorySection } from '../components/history/EquipmentHistorySection';
import { WorkOrderOriginLink } from '../components/WorkOrderOriginLink';
import { WorkOrderPrintButton } from '../components/WorkOrderPrintButton';
import PMPackagesPanel from '../components/PMPackagesPanel';
import OperatingHoursCard from '../components/OperatingHoursCard';
import ExpandableText from '../components/ExpandableText';
import CopyApplicablePartsDialog from '../components/CopyApplicablePartsDialog';
import AddWorkOrderDialog from '../components/AddWorkOrderDialog';
import EditWorkOrderDialog from '../components/EditWorkOrderDialog';
import CompleteWorkOrderDialog from '../components/CompleteWorkOrderDialog';
import { Separator } from '../components/ui/separator';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import HeaderMenu from '../components/HeaderMenu';
import PinButton from '../components/PinButton';
import { usePins } from '../lib/PinsContext';
import { getPriorityLabel, getPriorityBadgeClass } from '../lib/priority';

const MachineryDetail = ({ user, onLogout }) => {
  const { pinnedIds, setPinnedFromArray } = usePins();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const focusedWO = searchParams.get('workOrder');
  const navigate = useNavigate();
  const [machinery, setMachinery] = useState(null);
  const [logs, setLogs] = useState([]);
  const [historyEvents, setHistoryEvents] = useState([]);
  const [historyError, setHistoryError] = useState('');
  const [deletingHistory, setDeletingHistory] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);
  const [installationPoints, setInstallationPoints] = useState([]);
  const [applicableParts, setApplicableParts] = useState([]);
  const [applicableCollapsed, setApplicableCollapsed] = useState(() => {
    try { return localStorage.getItem('applicablePartsCollapsed') === '1'; } catch { return false; }
  });
  const [openWOsCollapsed, setOpenWOsCollapsed] = useState(() => {
    try { return localStorage.getItem('openWorkOrdersCollapsed') === '1'; } catch { return false; }
  });
  const [showCopyPartsDialog, setShowCopyPartsDialog] = useState(false);
  const [showAddLogDialog, setShowAddLogDialog] = useState(false);
  const [showEditLogDialog, setShowEditLogDialog] = useState(false);
  const [showAddWorkOrderDialog, setShowAddWorkOrderDialog] = useState(false);
  const [showEditWorkOrderDialog, setShowEditWorkOrderDialog] = useState(false);
  const [completingWorkOrder, setCompletingWorkOrder] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [loading, setLoading] = useState(true);

  const isAdmin = user.role === 'admin' || user.role === 'technician';
  const canLogHours = ['admin', 'technician', 'operator'].includes(user.role);

  useEffect(() => {
    fetchData();
    window.addEventListener('maintenance-records-changed', fetchData);
    return () => window.removeEventListener('maintenance-records-changed', fetchData);
  }, [id]);

  useEffect(() => {
    if (focusedWO && workOrders.some((wo) => wo.id === focusedWO && wo.status !== 'completed')) {
      setOpenWOsCollapsed(false);
      requestAnimationFrame(() => document.getElementById(`work-order-${focusedWO}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
    }
  }, [focusedWO, workOrders]);

  const goBack = () => {
    if (machinery?.plant_location_id) {
      navigate(`/location/${machinery.plant_location_id}`);
    } else {
      navigate('/');
    }
  };

  const fetchData = async () => {
    try {
      const [machineryRes, logsRes, workOrdersRes, applicablePartsRes, ipRes, historyRes] = await Promise.all([
        axios.get(`${API}/machinery/${id}`),
        axios.get(`${API}/maintenance-logs/machinery/${id}`),
        axios.get(`${API}/work-orders/machinery/${id}`),
        // Read all inventory once and filter client-side — small dataset.
        axios.get(`${API}/inventory`).catch(() => ({ data: [] })),
        axios.get(`${API}/machinery/${id}/installation-points`).catch(() => ({ data: [] })),
        axios.get(`${API}/maintenance-history?machinery_id=${id}`).catch(() => ({ data: [], failed: true })),
      ]);
      setMachinery(machineryRes.data);
      setLogs(logsRes.data);
      setHistoryEvents(historyRes.data);
      setHistoryError(historyRes.failed ? 'History could not be loaded.' : '');
      setWorkOrders(workOrdersRes.data);
      setInstallationPoints(ipRes.data || []);
      setApplicableParts(
        (applicablePartsRes.data || []).filter((p) =>
          (p.applicable_machinery_ids || []).includes(id)
        )
      );
    } catch (error) {
      toast.error('Failed to load machinery details');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/machinery/${id}`);
      toast.success('Equipment deleted successfully');
      goBack();
    } catch (error) {
      toast.error('Failed to delete equipment');
    }
  };

  const handleDeleteLog = async (logId) => {
    try {
      await axios.delete(`${API}/maintenance-logs/${logId}`);
      toast.success('Maintenance log deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete log');
    }
  };

  const handleEditLog = (log) => {
    setSelectedLog(log);
    setShowEditLogDialog(true);
  };

  const handleEditWorkOrder = (workOrder) => {
    setSelectedWorkOrder(workOrder);
    setShowEditWorkOrderDialog(true);
  };

  const openHistoryLog = async (logId) => {
    try { handleEditLog(logs.find((log) => log.id === logId) || (await axios.get(`${API}/maintenance-logs/${logId}`)).data); }
    catch { toast.error('Could not load maintenance log'); }
  };
  const openHistoryWorkOrder = async (woId) => {
    try { handleEditWorkOrder(workOrders.find((wo) => wo.id === woId) || (await axios.get(`${API}/work-orders/${woId}`)).data); }
    catch { toast.error('Could not load work order'); }
  };

  const handleDeleteWorkOrder = async (workOrderId) => {
    try {
      await axios.delete(`${API}/work-orders/${workOrderId}`);
      toast.success('Work order deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete work order');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      operational: { label: 'Operational', className: 'bg-green-500 hover:bg-green-600' },
      maintenance_needed: { label: 'Maintenance Needed', className: 'bg-yellow-500 hover:bg-yellow-600' },
      down: { label: 'Down', className: 'bg-red-500 hover:bg-red-600' },
      lockout: { label: 'Lockout', className: 'bg-orange-500 hover:bg-orange-600' }
    };

    const config = statusConfig[status] || { label: status, className: 'bg-gray-500' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const getWorkOrderStatusBadge = (status) => {
    const statusConfig = {
      not_started: { label: 'Not Started', className: 'bg-slate-500' },
      in_progress: { label: 'In Progress', className: 'bg-blue-500' },
      on_hold: { label: 'On Hold', className: 'bg-sky-300 text-sky-950' },
      completed: { label: 'Completed', className: 'bg-green-500' }
    };

    const config = statusConfig[status] || { label: status, className: 'bg-gray-500' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    if (!priority) return null;
    return <Badge className={getPriorityBadgeClass(priority)}>{getPriorityLabel(priority)}</Badge>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-lg text-slate-600">Loading...</div>
      </div>
    );
  }

  if (!machinery) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap justify-between items-center gap-3">
            <div className="flex items-center gap-4 min-w-0">
              <Button variant="outline" onClick={goBack} data-testid="back-btn">
                ← Back
              </Button>
              <div className="min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <h1 className="text-2xl font-bold text-slate-900 break-words min-w-0" data-testid="equipment-page-title">{machinery.component}</h1>
                <PinButton
                  machineryId={machinery.id}
                  pinned={pinnedIds.has(machinery.id)}
                  onChange={(_next, ids) => setPinnedFromArray(ids)}
                />
              </div>
                <p className="text-sm text-slate-600 break-all">Serial: {machinery.serial_number}</p>
              </div>
            </div>
            <HeaderMenu user={user} onLogout={onLogout} />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Equipment Details */}
          <div className="lg:col-span-1 space-y-6 min-w-0">
            <Card data-testid="machinery-details-card">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <CardTitle>Equipment Details</CardTitle>
                  {getStatusBadge(machinery.status)}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {machinery.main_image && (
                  <div className="w-full h-48 overflow-hidden rounded-lg">
                    <img
                      src={imgSrc(machinery.main_image)}
                      alt={machinery.component}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                <div className="space-y-3 text-sm">
                  <div>
                    <div className="text-slate-600">Component</div>
                    <div className="font-medium">{machinery.component}</div>
                  </div>
                  <Separator />
                  <div>
                    <div className="text-slate-600">Local Location</div>
                    <div className="font-medium">{machinery.location}</div>
                  </div>
                  <Separator />
                  <div>
                    <div className="text-slate-600">Manufacturer</div>
                    <div className="font-medium">{machinery.manufacturer}</div>
                  </div>
                  <Separator />
                  <div>
                    <div className="text-slate-600">Model</div>
                    <div className="font-medium">{machinery.model}</div>
                  </div>
                  <Separator />
                  <div>
                    <div className="text-slate-600">Serial Number</div>
                    <div className="font-medium font-mono text-xs">{machinery.serial_number}</div>
                  </div>
                  {machinery.notes && (
                    <>
                      <Separator />
                      <div>
                        <div className="text-slate-600">Notes</div>
                        <div className="font-medium">{machinery.notes}</div>
                      </div>
                    </>
                  )}
                </div>

                {isAdmin && (
                  <div className="space-y-2 pt-4">
                    <Button
                      className="w-full"
                      onClick={() => setShowEditDialog(true)}
                      data-testid="edit-machinery-btn"
                    >
                      Edit Equipment
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" className="w-full" data-testid="delete-machinery-btn">
                          Delete Equipment
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete this equipment and all associated maintenance logs.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={handleDelete} data-testid="confirm-delete-btn">
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Operating Hours — manual meter readings, trendable for hours-based PMs. */}
            <OperatingHoursCard
              machineryId={id}
              canLog={canLogHours}
              isAdmin={isAdmin}
              onChanged={fetchData}
            />

            {/* Applicable Parts Section — collapsible, placed under Equipment Details. */}
            <Card data-testid="applicable-parts-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => {
                      setApplicableCollapsed((prev) => {
                        const next = !prev;
                        try { localStorage.setItem('applicablePartsCollapsed', next ? '1' : '0'); } catch { /* noop */ }
                        return next;
                      });
                    }}
                    className="flex items-center gap-2 text-left"
                    data-testid="applicable-parts-toggle"
                    aria-expanded={!applicableCollapsed}
                  >
                    <span className="text-slate-400 text-sm w-4">
                      {applicableCollapsed ? '▸' : '▾'}
                    </span>
                    <CardTitle className="text-base">
                      Applicable Parts
                      <span className="text-sm font-normal text-slate-500 ml-2">
                        ({applicableParts.length})
                      </span>
                    </CardTitle>
                  </button>
                  <div className="flex gap-2">
                    {isAdmin && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowCopyPartsDialog(true)}
                        data-testid="copy-applicable-parts-btn"
                      >
                        Copy from…
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/inventory?applicable=${id}`)}
                      data-testid="view-in-inventory-btn"
                    >
                      View in Inventory →
                    </Button>
                  </div>
                </div>
              </CardHeader>
              {!applicableCollapsed && (
                <CardContent data-testid="applicable-parts-body">
                  {applicableParts.length === 0 ? (
                    <div className="text-sm text-slate-500 italic">
                      No parts tagged as applicable to this equipment yet. Edit any inventory part and pick this equipment in the &quot;Applies to equipment&quot; field.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-2">
                      {applicableParts
                        .slice()
                        .sort((a, b) => (a.part_number || '').localeCompare(b.part_number || ''))
                        .map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => navigate(`/inventory?applicable=${id}&highlight=${p.id}`)}
                            className="text-left flex items-center gap-2 border rounded-md p-2 hover:bg-slate-50 hover:border-slate-400 transition"
                            data-testid={`applicable-part-${p.id}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-sm font-semibold">{p.part_number}</span>
                                {p.quantity === 0 && !p.non_critical && (
                                  <Badge className="bg-red-500 text-white text-[10px] px-1.5 py-0">Out</Badge>
                                )}
                                {p.quantity > 0 && p.reorder_level != null && p.quantity <= p.reorder_level && !p.non_critical && (
                                  <Badge className="bg-yellow-500 text-white text-[10px] px-1.5 py-0">Low</Badge>
                                )}
                              </div>
                              <div className="text-xs text-slate-600 truncate">{p.name}</div>
                            </div>
                            <div className="text-xs text-slate-500 shrink-0">
                              {p.quantity} {p.unit}
                            </div>
                          </button>
                        ))}
                    </div>
                  )}
                </CardContent>
              )}
            </Card>

            {/* PM Packages — parts kits checked against live inventory. */}
            <PMPackagesPanel machineryId={id} isAdmin={isAdmin} />
          </div>

          {/* Right Column - Installation Points, Work Orders and Maintenance Logs */}
          <div className="lg:col-span-2 space-y-6 min-w-0">
            {/* Installation Points */}
            <InstallationPointsPanel
              refreshKey={installationPoints}
              machineryId={id}
              equipmentName={machinery.component}
              canEdit={isAdmin}
            />

            {/* Work Orders (open only — completed flow into Maintenance History) */}
            {(() => {
              const openWOs = workOrders.filter((wo) => wo.status !== 'completed');
              return (
                <Card>
                  <CardHeader>
                    <div className="flex justify-between items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => {
                          setOpenWOsCollapsed((prev) => {
                            const next = !prev;
                            try { localStorage.setItem('openWorkOrdersCollapsed', next ? '1' : '0'); } catch { /* noop */ }
                            return next;
                          });
                        }}
                        className="flex items-center gap-2 text-left"
                        data-testid="open-work-orders-toggle"
                        aria-expanded={!openWOsCollapsed}
                      >
                        <span className="text-slate-400 text-sm w-4">
                          {openWOsCollapsed ? '▸' : '▾'}
                        </span>
                        <CardTitle>
                          Open Work Orders
                          <span className="text-sm font-normal text-slate-500 ml-2">
                            ({openWOs.length})
                          </span>
                        </CardTitle>
                      </button>
                      {isAdmin && (
                        <Button onClick={() => setShowAddWorkOrderDialog(true)} data-testid="add-work-order-btn">
                          Add Work Order
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  {!openWOsCollapsed && (
                  <CardContent data-testid="open-work-orders-body">
                    {openWOs.length === 0 ? (
                      <div className="text-center py-8 text-slate-600">
                        No open work orders. Completed ones appear in Maintenance History below.
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {openWOs.map((wo) => (
                      <div
                        key={wo.id}
                        id={`work-order-${wo.id}`}
                        className="border rounded-lg p-4 space-y-3"
                        data-testid={`work-order-${wo.id}`}
                      >
                        <div className="flex flex-wrap justify-between items-start gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              {wo.wo_number && (
                                <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600" data-testid={`wo-number-${wo.id}`}>
                                  #{wo.wo_number}
                                </span>
                              )}
                              <div className="font-semibold text-lg break-words min-w-0">{wo.title}</div>
                            </div>
                            <div className="text-sm text-slate-600 mt-1">
                              Created by {wo.created_by_name} • {formatDate(wo.created_at)}
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            {getWorkOrderStatusBadge(wo.status)}
                            {wo.priority && getPriorityBadge(wo.priority)}
                            <WorkOrderPrintButton workOrderId={wo.id} />
                            {isAdmin && (
                              <div className="flex gap-1 ml-2">
                                {wo.status !== 'completed' && (
                                  <Button
                                    size="sm"
                                    onClick={() => setCompletingWorkOrder(wo)}
                                    data-testid={`complete-work-order-btn-${wo.id}`}
                                    className="bg-green-600 hover:bg-green-700"
                                  >
                                    Complete
                                  </Button>
                                )}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEditWorkOrder(wo)}
                                  data-testid={`edit-work-order-btn-${wo.id}`}
                                >
                                  Edit
                                </Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      data-testid={`delete-work-order-btn-${wo.id}`}
                                    >
                                      Delete
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Delete Work Order?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        This action cannot be undone.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => handleDeleteWorkOrder(wo.id)}>
                                        Delete
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            )}
                          </div>
                        </div>

                        <WorkOrderOriginLink record={wo} testId={`wo-origin-${wo.id}`} />
                        {wo.description && (
                          <ExpandableText text={wo.description} className="text-sm text-slate-700" />
                        )}

                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {wo.due_date && (
                            <div>
                              <span className="text-slate-600">Due: </span>
                              <span className="font-medium">{formatDate(wo.due_date)}</span>
                            </div>
                          )}
                          {wo.assigned_to && (
                            <div>
                              <span className="text-slate-600">Assigned to: </span>
                              <span className="font-medium">{wo.assigned_to}</span>
                            </div>
                          )}
                          {wo.frequency_days && (
                            <div>
                              <span className="text-slate-600">Recurs: </span>
                              <span className="font-medium">Every {wo.frequency_days} days</span>
                            </div>
                          )}
                          {wo.frequency_hours && (
                            <div>
                              <span className="text-slate-600">Recurs: </span>
                              <span className="font-medium">Every {Number(wo.frequency_hours).toLocaleString()} run hrs</span>
                            </div>
                          )}
                          {wo.hours_due_at != null && (
                            <div data-testid={`wo-hours-due-${wo.id}`}>
                              <span className="text-slate-600">Due at: </span>
                              <span className="font-medium">{Number(wo.hours_due_at).toLocaleString()} hrs</span>
                              {wo.current_hours != null && (
                                <span className="text-xs text-slate-500"> (now {Number(wo.current_hours).toLocaleString()})</span>
                              )}
                            </div>
                          )}
                          {wo.projected_hours_due_date && (
                            <div data-testid={`wo-hours-projection-${wo.id}`}>
                              <span className="text-slate-600">Hours proj.: </span>
                              <span className="font-medium">
                                ≈ {new Date(wo.projected_hours_due_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                              </span>
                            </div>
                          )}
                          {wo.reminder_enabled && (
                            <div>
                              <span className="text-slate-600">Reminders: </span>
                              <span className="font-medium">On</span>
                            </div>
                          )}
                        </div>

                        {wo.notes && (
                          <div className="text-sm border-t pt-2 mt-2">
                            <span className="text-slate-600">Notes: </span>
                            <span className="whitespace-pre-wrap break-words">{wo.notes}</span>
                          </div>
                        )}
                      </div>
                    ))}
                      </div>
                    )}
                  </CardContent>
                  )}
                </Card>
              );
            })()}

            <EquipmentHistorySection title="Recently Completed Work Orders" testId="completed-work-orders-card"
              events={historyEvents.filter((e) => e.event_type === 'completed_work_order')}
              focusId={focusedWO} isAdmin={isAdmin} error={historyError} onRetry={fetchData}
              onEditWorkOrder={openHistoryWorkOrder} onDeleteWorkOrder={(recordId) => setDeletingHistory({ kind: 'work_order', id: recordId })} />
            <EquipmentHistorySection title="Maintenance History" testId="equipment-maintenance-history"
              events={historyEvents.filter((e) => e.event_type === 'maintenance_log')}
              isAdmin={isAdmin} error={historyError} onRetry={fetchData}
              onAddLog={isAdmin ? () => setShowAddLogDialog(true) : undefined}
              onEditLog={openHistoryLog} onDeleteLog={(recordId) => setDeletingHistory({ kind: 'log', id: recordId })} />
          </div>
        </div>
      </div>

      {isAdmin && (
        <>
          <AlertDialog open={!!deletingHistory} onOpenChange={(open) => !open && setDeletingHistory(null)}>
            <AlertDialogContent data-testid="equipment-history-delete-dialog">
              <AlertDialogHeader><AlertDialogTitle>Delete maintenance record?</AlertDialogTitle>
                <AlertDialogDescription>This cannot be undone. Consumed inventory and serialized placements are not restored.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel data-testid="equipment-history-delete-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="equipment-history-delete-confirm" onClick={() => {
                  if (deletingHistory.kind === 'log') handleDeleteLog(deletingHistory.id);
                  else handleDeleteWorkOrder(deletingHistory.id);
                  setDeletingHistory(null);
                }}>Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <AddMaintenanceDialog
            open={showAddLogDialog}
            onClose={() => setShowAddLogDialog(false)}
            machineryId={id}
            installationPoints={installationPoints}
            onSuccess={() => {
              setShowAddLogDialog(false);
              fetchData();
            }}
          />
          <EditMaintenanceDialog
            open={showEditLogDialog}
            onClose={() => {
              setShowEditLogDialog(false);
              setSelectedLog(null);
            }}
            log={selectedLog}
            installationPoints={installationPoints}
            onSuccess={() => {
              setShowEditLogDialog(false);
              setSelectedLog(null);
              fetchData();
            }}
          />
          <EditMachineryDialog
            open={showEditDialog}
            onClose={() => setShowEditDialog(false)}
            machinery={machinery}
            onSuccess={() => {
              setShowEditDialog(false);
              fetchData();
            }}
          />
          <AddWorkOrderDialog
            open={showAddWorkOrderDialog}
            onClose={() => setShowAddWorkOrderDialog(false)}
            machineryId={id}
            onSuccess={() => {
              setShowAddWorkOrderDialog(false);
              fetchData();
            }}
          />
          <EditWorkOrderDialog
            open={showEditWorkOrderDialog}
            onClose={() => {
              setShowEditWorkOrderDialog(false);
              setSelectedWorkOrder(null);
            }}
            workOrder={selectedWorkOrder}
            onSuccess={() => {
              setShowEditWorkOrderDialog(false);
              setSelectedWorkOrder(null);
              fetchData();
            }}
          />
          <CompleteWorkOrderDialog
            open={!!completingWorkOrder}
            workOrder={completingWorkOrder}
            onClose={() => setCompletingWorkOrder(null)}
            onSuccess={() => {
              setCompletingWorkOrder(null);
              fetchData();
            }}
          />
          <CopyApplicablePartsDialog
            open={showCopyPartsDialog}
            onClose={() => setShowCopyPartsDialog(false)}
            machineryId={id}
            machineryName={machinery.component}
            onSuccess={() => {
              setShowCopyPartsDialog(false);
              fetchData();
            }}
          />
        </>
      )}
    </div>
  );
};

export default MachineryDetail;
